'use client';

import Link from 'next/link';
import {
  Landmark, BarChart3, Layout, BookOpen, Compass,
  ShieldCheck, Check, FileText, Search, Users,
} from 'lucide-react';
import { ScrollReveal } from '@/components/ScrollReveal';

const suiteFeatures = [
  {
    icon: BarChart3,
    title: 'Value Profile',
    desc: 'Distill your career into level-appropriate impact areas — each one backed by proof from your real record, with a positioning headline, a 30-second pitch, and an executive bio.',
  },
  {
    icon: Compass,
    title: 'Role Playbooks',
    desc: 'How CISO, VP, and C-suite roles actually get filled: the search firms that place them, the communities that matter, what to avoid, and positioning tips — with cited sources.',
  },
  {
    icon: Search,
    title: 'AI Role Research',
    desc: 'No playbook for your target role yet? The AI researches the market for it — search firms, fractional rates, remote outlook — and every claim is checked before you see it.',
  },
  {
    icon: FileText,
    title: 'Career Snapshot',
    desc: 'Metrics, impact areas, and timeline on one page. Send it when networking or introducing yourself to a search firm.',
  },
  {
    icon: Layout,
    title: 'Leadership One-Pager',
    desc: 'Impact areas with proof and your leadership style. Bring it to interviews as a leave-behind that resumes can’t match.',
  },
  {
    icon: BookOpen,
    title: 'Executive Bio & Services One-Pager',
    desc: 'A narrative bio for board, investor, and speaking intros — plus a services one-pager that frames you for fractional and consulting engagements.',
  },
];

const serviceComparison = [
  { aspect: 'Price', human: '$1,500–$3,000 one-time', spr: '$49/month, cancel anytime' },
  { aspect: 'Turnaround', human: '2–4 weeks of interviews and drafts', spr: 'First documents in minutes' },
  { aspect: 'Updates', human: 'New engagement, new invoice', spr: 'Regenerate for every role, unlimited' },
  { aspect: 'Tailoring per role', human: 'One master resume', spr: 'Tailored resume for every job description' },
  { aspect: 'Fact-checking', human: 'Writer’s notes from a phone call', spr: 'Every claim verified against your real record' },
  { aspect: 'Beyond the resume', human: 'Resume and cover letter', spr: 'Bios, one-pagers, playbooks, interview prep' },
];

export default function ExecutiveContent() {
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-white to-stone-100 py-16 px-4 flex flex-col items-center">
      <main className="w-full max-w-5xl mx-auto">
        {/* Hero */}
        <ScrollReveal>
          <section className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              <Landmark className="w-4 h-4" />
              The Executive Plan
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight mb-6">
              Executive Positioning,<br className="hidden md:block" /> Not Just a Resume
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              At the senior level, a resume is table stakes. The Executive Suite turns
              your career record into value profiles, board-ready documents, and role
              playbooks — every claim verified against what you actually did.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.superpowerresume.com/sign-up"
                className="inline-block bg-gray-900 text-white font-bold py-3 px-8 rounded-xl shadow-lg text-lg hover:bg-gray-800 hover:scale-105 hover:shadow-xl transition-all duration-200"
              >
                Start Executive — $49/mo
              </a>
              <Link
                href="/pricing"
                className="inline-block border-2 border-gray-300 text-gray-700 font-bold py-3 px-8 rounded-xl text-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                Compare Plans
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-4">$41/month billed annually. Everything in Premium, unlimited.</p>
          </section>
        </ScrollReveal>

        {/* Executive Suite features */}
        <section className="mb-20">
          <ScrollReveal>
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
              The Executive Suite
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Six capabilities built for senior leaders — all generated from your real
              career record, not a template.
            </p>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {suiteFeatures.map(({ icon: Icon, title, desc }, i) => (
              <ScrollReveal key={title} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full">
                  <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-gray-600">{desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* Verification — fact-checked harder, not less */}
        <ScrollReveal>
          <section className="mb-20">
            <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl px-8 py-14 border border-gray-700">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(59,130,246,0.15),_transparent_50%)]" />
              <div className="relative z-10 max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-md">
                    <ShieldCheck className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                    Fact-Checked Harder, Not Less
                  </h2>
                </div>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  Executive claims get scrutinized — by boards, by search firms, by
                  reference checks. So the same truth guarantees that protect every
                  Superpower Resume user protect you most of all.
                </p>
                <ul className="space-y-4">
                  {[
                    'Numbers can only come from your real career data — the AI is structurally unable to invent a metric.',
                    'Every claim in every document is verified against your record after generation and labeled verified, unverified, or conflicting.',
                    'Hover any verified claim to see its provenance — exactly which role and company it traces to.',
                    'Role playbooks and AI role research ship with cited sources, so you can check the market intelligence yourself.',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-gray-300">
                      <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" strokeWidth={3} />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* vs human executive-resume services */}
        <ScrollReveal>
          <section className="mb-20">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
              Executive Resume Writers Charge $1,500+. Once.
            </h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              A human service hands you one document and a goodbye. Executive is a
              living system that re-positions you for every role, all year, for less
              than the cost of one engagement.
            </p>
            <div className="rounded-2xl shadow-lg bg-white border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-4 text-left font-bold text-gray-800" />
                      <th className="px-6 py-4 text-center font-bold text-gray-500 w-56">
                        <div className="flex items-center justify-center gap-2">
                          <Users className="w-4 h-4" /> Human Resume Service
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center font-bold text-blue-700 w-56">
                        <div className="flex items-center justify-center gap-2">
                          <Landmark className="w-4 h-4" /> Executive Plan
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceComparison.map(({ aspect, human, spr }, idx) => (
                      <tr
                        key={aspect}
                        className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                      >
                        <td className="px-6 py-4 font-medium text-gray-900">{aspect}</td>
                        <td className="px-6 py-4 text-center text-gray-500">{human}</td>
                        <td className="px-6 py-4 text-center text-blue-700 font-medium">{spr}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* What's included */}
        <ScrollReveal>
          <section className="mb-20">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 px-8 py-12">
              <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
                Everything in Premium. Unlimited.
              </h2>
              <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
                The Executive Suite sits on top of the full platform — with every
                usage limit removed.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
                {[
                  'Unlimited tailored resumes & cover letters',
                  'Unlimited document uploads',
                  'Unlimited voice mock interviews — including executive rounds',
                  'Unlimited AI chat revisions',
                  'Unlimited AI headshot enhancements',
                  'Full LinkedIn optimization suite',
                  'Advanced ATS scoring & keyword gap analysis',
                  'Claim verification on every document',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Final CTA */}
        <ScrollReveal>
          <section className="flex justify-center">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl shadow-2xl px-8 py-12 max-w-xl w-full flex flex-col items-center border border-gray-700">
              <h2 className="text-3xl font-extrabold mb-4 text-center text-white tracking-tight">
                Ready to position, not just apply?
              </h2>
              <p className="text-lg text-gray-300 mb-8 text-center">
                Executive is $49/month — or $41/month billed annually. Cancel anytime.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://app.superpowerresume.com/sign-up"
                  className="inline-block bg-white text-gray-900 font-bold py-3 px-8 rounded-xl shadow text-lg hover:bg-gray-100 hover:scale-105 hover:shadow-xl transition-all duration-200 text-center"
                >
                  Start Executive
                </a>
                <Link
                  href="/pricing"
                  className="inline-block border-2 border-gray-500 text-gray-200 font-bold py-3 px-8 rounded-xl text-lg hover:border-white hover:text-white transition-colors text-center"
                >
                  See All Plans
                </Link>
              </div>
            </div>
          </section>
        </ScrollReveal>
      </main>
    </div>
  );
}
